import 'react-day-picker/dist/style.css';

import { ArrowLeft as ArrowLeftIcon, ArrowRight as ArrowRightIcon } from '@mui/icons-material';
import {
  Box,
  IconButton,
  LinearProgress,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material';
import makeStyles from '@mui/styles/makeStyles';
import { DatePicker } from 'antd';
import classNames from 'classnames';
import {
  addDays,
  differenceInDays,
  endOfDay,
  endOfMonth,
  format,
  getUnixTime,
  isFuture,
  isToday,
  startOfDay,
  startOfMonth,
  subDays,
} from 'date-fns';
import dayjs from 'dayjs';
import moment from 'moment-timezone';
import { useEffect, useState } from 'react';
import React from 'react';
import { useParams } from 'react-router-dom';
import { toast } from 'react-toastify';

import { usePersistedDateRange } from '@/hooks/usePersistedDateRange';
import { usePersistedUsageGranularity } from '@/hooks/usePersistedUsageGranularity';
import useSWR from 'swr';

import useMixPanel from '@/hooks/useMixPanel';
// Types
import { defaultOutageStatusResponse, getOutageStatus } from '@/Outage/api';
import {
  mapOutageStatusToIsElasticsearchOutage,
  OUTAGE_STATUS_REFRESH_MILLIS,
  OUTAGE_STATUS_SWR_KEY,
} from '@/Outage/util';
import { useUserPermissions } from '@/hooks/useUserPermissions';
import { get as getUsage, getHourly as getHourlyUsage } from '@/Usage/api';

import { Chart } from '../../../components/Chart';
import {
  buildEstimatedChartData,
  createLabel as createChartLabel,
} from '../../../components/Chart/util';
import { ErrorBoundary } from '../../../components/ErrorBoundary';
import { UsageTotalHeadline } from '../../../components/UsageTotalHeadline';
import { getTotalVolume } from '../../../Usage/util';
import { addCreate, convertTimestampToTimeZone } from '../../../util';
import { useStructure } from '../../useStructures';
import { generateHourlyUsageCSV, generateUsageCSV } from '../../util';
const { RangePicker } = DatePicker;

const dateFormat = 'YYYY-MM-DD';

const useStyles = makeStyles((theme) => ({
  wrapper: {
    display: 'flex',
    width: '100%',
  },
  container: {
    display: 'flex',
    flexDirection: 'column',
    width: '100%',
  },
  header: {
    alignItems: 'baseline',
    display: 'flex',
    justifyContent: 'space-between',
    paddingBottom: 0,
    paddingLeft: 0,
    textAlign: 'left',
  },
  chartBody: {
    alignItems: 'center',
    display: 'flex',
    justifyContent: 'center',
  },
  chartLoader: {
    // Chart has a fixed width of 700px (see components/Chart/index.tsx, VictoryContainer
    // responsive=false). Anchor the loader to that, not to chartBody's variable width,
    // so the bar's length is consistent across all account contexts and view layouts.
    // 490px is ~70% of the chart width — visible without overshooting either axis.
    width: 490,
    left: '50%',
    top: '50%',
    transform: 'translate(-50%, -50%)',
    position: 'absolute',
  },
  selectedDays: {
    fontWeight: 'bold',
    backgroundColor: theme.palette.primary.main,
  },
  editIcon: {
    marginLeft: theme.spacing(1),
    marginTop: theme.spacing(-0.5),
    opacity: 0.3,
    '&:hover': {
      opacity: 1,
    },
  },
  prevNextButton: {
    height: 'fit-content',
  },
  nextButton: {
    marginLeft: theme.spacing(1),
  },
  prevButton: {
    marginRight: theme.spacing(1),
  },
}));

const today = moment().toDate();

interface IProps {
  timeZone: string;
}

export const UsageTab = ({ timeZone }: IProps): JSX.Element => {
  const classes = useStyles();

  const [graphData, setGraphData] = useState([
    { label: '', gallons: 0, volume: 0, date: format(today, 'yyyy-MM-dd') },
  ]);
  const { range, setRange, moveByMonth } = usePersistedDateRange();
  const [canGoNext, setCanGoNext] = useState(false);
  const { trackEvent } = useMixPanel();
  const [eventData, setEventData] = React.useState<{}>();
  const { structureId } = useParams();
  if (!structureId) {
    throw new Error('MISSING_REQUIRED_ROUTE_DATA');
  }

  // Data fetching ////////////////////

  const { structure } = useStructure(structureId);

  React.useEffect(() => {
    if (structure && range) {
      trackEvent('Property Usage Viewed', {
        PropertyID: structureId,
        PropertyName: structure.name,
        StartDate: convertTimestampToTimeZone(getUnixTime(startOfDay(range[0]!)), undefined),
        EndDate: convertTimestampToTimeZone(getUnixTime(startOfDay(range[1]!)), undefined),
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [structure, structureId, range]);

  /** For request that should be cached for a day */
  const STATIC_REQUEST_DEDUP_INTERVAL = 86400000; // Milliseconds in a day

  // Per-membership HOURLY_USAGE flag from /me/permissions. When true, the customer sees a
  // toggle that lets them switch between daily and hourly. The flag is granted by Customer
  // Support; customers without it never see the toggle and only see daily.
  // The granularity choice is persisted in sessionStorage so it carries across navigation
  // between properties/units (same pattern as `usePersistedDateRange`).
  const { hasHourlyUsage } = useUserPermissions();
  const { granularity: usageGranularity, setGranularity: setUsageGranularity } =
    usePersistedUsageGranularity();
  const showHourly = hasHourlyUsage && usageGranularity === 'hourly';

  const getRawUsageId = () => {
    if (!(structure && range[0] && range[1] && structure.units && structure.units.length > 0)) {
      return null;
    }
    if (showHourly) {
      return null;
    }
    return `structures/${structureId}/usage?start=${startOfDay(range[0]).getTime()}&end=${endOfDay(
      range[1],
    ).getTime()}`;
  };

  const getRawHourlyUsageId = () => {
    if (!(structure && range[0] && range[1] && structure.units && structure.units.length > 0)) {
      return null;
    }
    if (!showHourly) {
      return null;
    }
    return `structures/${structureId}/usage/hourly?start=${startOfDay(
      range[0],
    ).getTime()}&end=${endOfDay(range[1]).getTime()}`;
  };

  const { data: outageStatusResponse = defaultOutageStatusResponse } = useSWR(
    OUTAGE_STATUS_SWR_KEY,
    getOutageStatus,
    {
      refreshInterval: OUTAGE_STATUS_REFRESH_MILLIS,
    },
  );

  // Load daily usage (when caller does NOT have HOURLY_USAGE)
  const { data: rawUsage, error: usageError } = useSWR(
    getRawUsageId,
    async () => {
      try {
        const BATCH_SIZE = 20;
        const results: Awaited<ReturnType<typeof getUsage>>[] = [];
        for (let i = 0; i < structure!.units.length; i += BATCH_SIZE) {
          const batch = structure!.units.slice(i, i + BATCH_SIZE);
          const batchResults = await Promise.all(
            batch.map((unit) =>
              getUsage(
                unit.id,
                range[0]!,
                range[1]!,
                mapOutageStatusToIsElasticsearchOutage(outageStatusResponse),
                timeZone,
              ).catch((err) => {
                // For permission errors, reject the entire promise so useSWR can handle it
                if (err.message?.includes('PERMISSION_DENIED')) {
                  throw err;
                }
                // For other errors, return empty data to avoid breaking the UI
                return {
                  unit_id: unit.id,
                  unit_name: unit.name,
                  devices: [],
                  timezone_key: timeZone,
                  volume_units: 'gal',
                  estimated_bucket_volumes: [],
                };
              }),
            ),
          );
          results.push(...batchResults);
        }
        return results;
      } catch (err: any) {
        // If it's a permission error, throw it with a marker so useSWR captures it
        if (err.message?.includes('PERMISSION_DENIED')) {
          throw err;
        }
        // For other errors, throw them as well
        throw err;
      }
    },
    {
      dedupingInterval: STATIC_REQUEST_DEDUP_INTERVAL,
      shouldRetryOnError: false, // Don't retry on permission errors
    },
  );

  // Load hourly usage (when caller HAS HOURLY_USAGE). Same per-unit fan-out, just hits the
  // hourly endpoint and returns hourly_usages buckets.
  const { data: rawHourlyUsage, error: hourlyUsageError } = useSWR(
    getRawHourlyUsageId,
    async () => {
      try {
        const BATCH_SIZE = 20;
        const results: Awaited<ReturnType<typeof getHourlyUsage>>[] = [];
        for (let i = 0; i < structure!.units.length; i += BATCH_SIZE) {
          const batch = structure!.units.slice(i, i + BATCH_SIZE);
          const batchResults = await Promise.all(
            batch.map((unit) =>
              getHourlyUsage(
                unit.id,
                range[0]!,
                range[1]!,
                mapOutageStatusToIsElasticsearchOutage(outageStatusResponse),
                timeZone,
              ).catch((err) => {
                if (err.message?.includes('PERMISSION_DENIED')) {
                  throw err;
                }
                return {
                  unit_id: unit.id,
                  unit_name: unit.name,
                  devices: [],
                  timezone_key: timeZone,
                  volume_units: 'gal',
                  estimated_bucket_volumes: [],
                };
              }),
            ),
          );
          results.push(...batchResults);
        }
        return results;
      } catch (err: any) {
        if (err.message?.includes('PERMISSION_DENIED')) {
          throw err;
        }
        throw err;
      }
    },
    {
      dedupingInterval: STATIC_REQUEST_DEDUP_INTERVAL,
      shouldRetryOnError: false,
    },
  );

  // Check if error is permission denied (covers both daily and hourly)
  const isPermissionDenied = (usageError ?? hourlyUsageError)?.message?.includes(
    'PERMISSION_DENIED',
  );

  // Set graph data when usage changes.
  // Daily mode: one chart point per date, aggregated across all units in the property.
  // Hourly mode: one chart point per hour bucket (with `xValue` for the Chart's time-scale axis).
  useEffect(() => {
    if (showHourly) {
      const usageByHour: { [hourEpoch: string]: number } = {};
      rawHourlyUsage?.forEach((usage) => {
        usage.devices?.forEach((device) => {
          device.hourly_usages.forEach((bucket) => {
            addCreate(usageByHour, String(bucket.time), bucket.volume);
          });
        });
      });
      const cleaned = Object.entries(usageByHour)
        .map(([epochSecondStr, vol]) => {
          const epochMs = Number(epochSecondStr) * 1000;
          const dateStr = format(new Date(epochMs), 'yyyy-MM-dd');
          // Bake the hourly tooltip label directly into the datum so VictoryTooltip
          // renders it via datum.label (more reliable across Victory versions than the
          // labels-callback prop). Time is rendered in the property's timezone.
          const tz = timeZone || moment.tz.guess();
          const labelTime = moment.tz(epochMs, tz).format('M/D/YYYY h:mm A');
          return {
            label: `${(vol as number).toFixed(2)} gallons used at ${labelTime}`,
            gallons: vol as number,
            volume: vol as number,
            date: dateStr,
            xValue: epochMs,
          };
        })
        .sort((a, b) => a.xValue - b.xValue);

      if (cleaned.length) {
        setGraphData(cleaned);
        setEventData({
          PropertyID: structureId,
          PropertyName: structure?.name,
          WaterUsage: getTotalVolume(cleaned),
          StartDate: convertTimestampToTimeZone(getUnixTime(startOfDay(range[0]!)), undefined),
          EndDate: convertTimestampToTimeZone(getUnixTime(startOfDay(range[1]!)), undefined),
        });
      } else {
        setGraphData([{ label: '', gallons: 0, volume: 0, date: format(range[0]!, 'yyyy-MM-dd') }]);
        setEventData({
          PropertyID: structureId,
          PropertyName: structure?.name,
          WaterUsage: getTotalVolume([{ volume: 0, date: format(range[0]!, 'yyyy-MM-dd') }]),
          StartDate: convertTimestampToTimeZone(getUnixTime(startOfDay(range[0]!)), undefined),
          EndDate: convertTimestampToTimeZone(getUnixTime(startOfDay(range[1]!)), undefined),
        });
      }
      return;
    }

    const usageByDate = {};
    rawUsage?.forEach((usage) => {
      usage.devices?.forEach((device) => {
        device.daily_usages.forEach((usageData) => {
          addCreate(usageByDate, usageData.date, usageData.volume);
        });
      });
    });

    const cleanedUsage = Object.entries(usageByDate).map((u) => {
      return {
        label: createChartLabel(u[1] as number, u[0]),
        gallons: u[1] as number,
        volume: u[1] as number,
        date: u[0],
      };
    });
    if (cleanedUsage.length) {
      setGraphData(cleanedUsage);
      setEventData({
        PropertyID: structureId,
        PropertyName: structure?.name,
        WaterUsage: getTotalVolume(cleanedUsage),
        StartDate: convertTimestampToTimeZone(getUnixTime(startOfDay(range[0]!)), undefined),
        EndDate: convertTimestampToTimeZone(getUnixTime(startOfDay(range[1]!)), undefined),
      });
    } else {
      setGraphData([{ label: '', gallons: 0, volume: 0, date: format(range[0]!, 'yyyy-MM-dd') }]);
      setEventData({
        PropertyID: structureId,
        PropertyName: structure?.name,
        WaterUsage: getTotalVolume([{ volume: 0, date: format(range[0]!, 'yyyy-MM-dd') }]),
        StartDate: convertTimestampToTimeZone(getUnixTime(startOfDay(range[0]!)), undefined),
        EndDate: convertTimestampToTimeZone(getUnixTime(startOfDay(range[1]!)), undefined),
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rawUsage, rawHourlyUsage, showHourly, range]);

  // En/disable next button
  useEffect(() => {
    if (range[1]) {
      if (isFuture(range[1]) || isToday(range[1])) {
        setCanGoNext(false);
      } else {
        setCanGoNext(true);
      }
    }
  }, [range]);

  const handleClickNext = () => {
    if (moveByMonth) {
      const newMonth = addDays(range[1]!, 3);
      setRange([startOfDay(startOfMonth(newMonth)), startOfDay(endOfMonth(newMonth))]);
    } else {
      const numberOfDaysInRange = differenceInDays(range[1]!, range[0]!);

      const newFrom = addDays(range[0]!, numberOfDaysInRange + 1);
      const newTo = addDays(range[1]!, numberOfDaysInRange + 1);

      setRange([startOfDay(newFrom), startOfDay(newTo)]);
    }
  };

  const handleClickPrev = () => {
    if (moveByMonth) {
      const newMonth = subDays(range[0]!, 3);
      setRange([startOfDay(startOfMonth(newMonth)), startOfDay(endOfMonth(newMonth))]);
    } else {
      const numberOfDaysInRange = differenceInDays(range[1]!, range[0]!);

      const newFrom = subDays(range[0]!, numberOfDaysInRange + 1);
      const newTo = subDays(range[1]!, numberOfDaysInRange + 1);

      setRange([startOfDay(newFrom), startOfDay(newTo)]);
    }
  };

  const handleDateChange = (dates) => {
    const startDate = dayjs(dates[0]).set('hour', 0).set('minute', 0).set('second', 0).toDate();
    const endDate = dayjs(dates[1]).set('hour', 23).set('minute', 59).set('second', 59).toDate();
    setRange([startDate, endDate]);
  };

  /** Whether or not usage is being fetched */
  // True while the relevant SWR fetch is in flight (rawUsage / rawHourlyUsage is undefined
  // until the first batch of per-unit responses arrives).
  const isLoadingUsage = showHourly ? !rawHourlyUsage : !rawUsage;

  // Estimated usage across ALL units in the property, aggregated per bucket in
  // gallons-space (buildEstimatedChartData sums entries sharing a bucket_start,
  // which is exactly the cross-unit aggregation the real series gets above).
  const estimatedGraphData = React.useMemo(() => {
    const perUnitResponses = showHourly ? rawHourlyUsage : rawUsage;
    // Prefix each unit's name into the reason: same-day estimates from several
    // units aggregate into ONE gray segment, so the tooltip must attribute the
    // split ("Unit A: meter offline; Unit B: vacancy estimate")
    const allEntries = (perUnitResponses ?? []).flatMap((usage) =>
      (usage.estimated_bucket_volumes ?? []).map((entry) => ({
        ...entry,
        reason: usage.unit_name ? `${usage.unit_name}: ${entry.reason}` : entry.reason,
      })),
    );
    return buildEstimatedChartData(
      allEntries,
      showHourly ? 'hourly' : 'daily',
      timeZone || moment.tz.guess(),
    );
  }, [rawUsage, rawHourlyUsage, showHourly, timeZone]);

  return (
    <ErrorBoundary>
      <div data-component-id={'STRUCTURE-VIEW'} className={classes.wrapper}>
        {structure && (
          <div className={classes.container}>
            {/* Chart */}
            <Box position="relative">
              <Box>
                <Box textAlign="center" className="custom-range-picker" marginBottom="15px">
                  <RangePicker
                    format={'MMM D, YYYY'}
                    allowClear={false}
                    maxDate={dayjs(format(endOfMonth(today), 'yyyy-MM-dd'), dateFormat)}
                    onChange={(value, dateString) => handleDateChange(dateString)}
                    value={
                      range && range[0] && range[1]
                        ? [
                            dayjs(format(range[0], 'yyyy-MM-dd'), dateFormat),
                            dayjs(format(range[1], 'yyyy-MM-dd'), dateFormat),
                          ]
                        : undefined
                    }
                    presets={[
                      {
                        label: <span aria-label="Current month">Current month</span>,
                        value: () => [dayjs().utc().startOf('month'), dayjs().utc().endOf('month')],
                      },
                    ]}
                  />
                </Box>
                {hasHourlyUsage && (
                  <Box textAlign="center" marginBottom="10px">
                    <ToggleButtonGroup
                      size="small"
                      exclusive
                      value={usageGranularity}
                      onChange={(_e, value) => {
                        if (value !== null) setUsageGranularity(value);
                      }}
                      aria-label="Usage granularity"
                    >
                      <ToggleButton value="daily" aria-label="Daily">
                        Daily
                      </ToggleButton>
                      <ToggleButton value="hourly" aria-label="Hourly">
                        Hourly
                      </ToggleButton>
                    </ToggleButtonGroup>
                  </Box>
                )}
                <UsageTotalHeadline
                  meteredVolume={getTotalVolume(graphData)}
                  estimatedVolume={getTotalVolume(estimatedGraphData || [])}
                />
              </Box>
              <Box className={classes.chartBody}>
                {isPermissionDenied ? (
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      minHeight: '400px',
                      width: '100%',
                    }}
                  >
                    <Typography variant="h6" color="text.secondary" align="center">
                      You don't have permission to view usage data.
                      <br />
                      Please contact your account owner.
                    </Typography>
                  </Box>
                ) : (
                  <>
                    <IconButton
                      size="large"
                      className={classNames(classes.prevNextButton, classes.prevButton)}
                      onClick={handleClickPrev}
                    >
                      <ArrowLeftIcon fontSize="large" />
                    </IconButton>
                    <Box
                      sx={{
                        opacity: isLoadingUsage ? 0.2 : undefined,
                        display: 'flex',
                        flexDirection: 'column',
                      }}
                    >
                      <Chart
                        data={graphData || []}
                        estimated={estimatedGraphData}
                        x={'date'}
                        y={'gallons'}
                        granularity={showHourly ? 'hourly' : 'daily'}
                        timezone={timeZone || undefined}
                        xDomain={
                          showHourly && range[0] && range[1]
                            ? [
                                moment
                                  .tz(format(range[0], 'yyyy-MM-dd'), timeZone || moment.tz.guess())
                                  .startOf('day')
                                  .valueOf(),
                                moment
                                  .tz(format(range[1], 'yyyy-MM-dd'), timeZone || moment.tz.guess())
                                  .endOf('day')
                                  .valueOf(),
                              ]
                            : undefined
                        }
                        createCSV={() => {
                          // Guard against clicking export before the SWR fetch resolves —
                          // otherwise the resulting CSV is just headers / empty.
                          const dataReady = showHourly ? !!rawHourlyUsage : !!rawUsage;
                          if (!dataReady) {
                            toast.info(
                              'Usage data is still loading. Please wait a moment and try again.',
                              {
                                closeButton: true,
                              },
                            );
                            return Promise.reject(new Error('Usage data not loaded yet'));
                          }
                          const csvMetadata = {
                            start: convertTimestampToTimeZone(
                              getUnixTime(range[0]!),
                              timeZone || Intl.DateTimeFormat().resolvedOptions().timeZone,
                              false,
                              undefined,
                              true,
                            ),
                            end: convertTimestampToTimeZone(
                              getUnixTime(range[1]!),
                              timeZone || Intl.DateTimeFormat().resolvedOptions().timeZone,
                              false,
                              undefined,
                              true,
                            ),
                            timezone: timeZone || Intl.DateTimeFormat().resolvedOptions().timeZone,
                            propertyName: structure?.name,
                            propertyAddress: structure?.address,
                          };
                          return Promise.resolve(
                            showHourly
                              ? generateHourlyUsageCSV(rawHourlyUsage, csvMetadata)
                              : generateUsageCSV(rawUsage, csvMetadata),
                          );
                        }}
                        eventType="Property Usage Exported"
                        eventData={eventData}
                        csvTitle={`${
                          moveByMonth
                            ? format(startOfMonth(range[0]!), 'MM_dd_yyyy')
                            : `${format(range[0]!, 'MM_dd_yyyy')}-${format(
                                range[1]!,
                                'MM_dd_yyyy',
                              )}`
                        }_${structure.name}_usage`}
                      />
                    </Box>
                    <IconButton
                      size="large"
                      className={classNames(classes.prevNextButton, classes.nextButton)}
                      onClick={handleClickNext}
                      disabled={!canGoNext}
                    >
                      <ArrowRightIcon fontSize="large" />
                    </IconButton>
                    {isLoadingUsage && (
                      <div className={classes.chartLoader}>
                        <LinearProgress />
                      </div>
                    )}
                  </>
                )}
              </Box>
            </Box>
          </div>
        )}
      </div>
    </ErrorBoundary>
  );
};
